import { runInAction } from "mobx"
import { formatUnits, isAddress } from "viem"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { ManualClPoolRepository } from "@/domains/thick-v2/manual-cl-repository"
import type { ThickV2TickDistributionStore } from "@/domains/thick-v2/tick-distribution"
import type {
  ManualClMyPositionsResponseItem,
  ManualClNfpmPositionRow,
} from "@/domains/validators"
import type { WalletConnectionStore } from "@/domains/wallet"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery } from "@/domains/library"
import {
  ManualClFeesCollectedEvent,
  ManualClLiquidityDecreasedEvent,
  ManualClLiquidityIncreasedEvent,
  ManualClPoolCreatedEvent,
  ManualClPositionBurnedEvent,
  ManualClPositionCreatedEvent,
} from "@/domains/thick-v2/events"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import { THICK_V2_BASE_ADDRESSES } from "../constants"
import type { ManualClPositionGraphQlTransport } from "./manual-cl-position-graphql-transport"
import type { ManualClPositionOnChainTransport } from "./manual-cl-position-on-chain-transport"
import type { ManualClPositionStoreDAO } from "./manual-cl-position-store"
import { ManualClPositionRepository } from "./manual-cl-position-repository"
import { ManualClPositionStore } from "./manual-cl-position-store"

/**
 * Ties together the three data sources that describe a user's position set:
 *
 *   1. GraphQL `getMyPositions` + `getMyNfpmPositions`
 *        — canonical static shape, nfpmTokenId, and indexer-derived
 *          underlying principal amounts (`principal0/1`) that replace the
 *          simulated `decreaseLiquidity` RPC.
 *   2. On-chain `nfpmUserStats`
 *        — pending fees only (tokensOwed0/1 can't be derived from events,
 *          so this remains the source of truth for claimable fees).
 *   3. Pool repository
 *        — current tick / sqrtPriceX96.
 *
 * Everything is stitched together through `OrchestratedQuery`s and
 * event-bus subscriptions for the six `manual-cl.*` lifecycle events. The
 * orchestrator never touches React; UI simply observes
 * `ManualClPositionRepository.activePositions`.
 */
type ManualClPositionsSyncPayload = {
  positions: Array<ManualClMyPositionsResponseItem>
  nfpmPositions: Array<ManualClNfpmPositionRow>
  ethPriceUsd: number | undefined
}

export class ManualClPositionOrchestrator {
  private graphQlQuery: OrchestratedQuery<ManualClPositionsSyncPayload> | undefined
  private onChainQuery: OrchestratedQuery<void> | undefined
  private nfpmDiscoveryQuery: OrchestratedQuery<void> | undefined

  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private poolCreatedHandler: ((event: ManualClPoolCreatedEvent) => void) | undefined
  private positionCreatedHandler: ((event: ManualClPositionCreatedEvent) => void) | undefined
  private liquidityIncreasedHandler: ((event: ManualClLiquidityIncreasedEvent) => void) | undefined
  private liquidityDecreasedHandler: ((event: ManualClLiquidityDecreasedEvent) => void) | undefined
  private feesCollectedHandler: ((event: ManualClFeesCollectedEvent) => void) | undefined
  private positionBurnedHandler: ((event: ManualClPositionBurnedEvent) => void) | undefined
  private postCreateRefreshTimers: Array<ReturnType<typeof setTimeout>> = []

  constructor(
    private readonly positionRepository: ManualClPositionRepository,
    private readonly poolRepository: ManualClPoolRepository,
    private readonly graphQlTransport: ManualClPositionGraphQlTransport,
    private readonly onChainTransport: ManualClPositionOnChainTransport,
    private readonly walletStore: WalletConnectionStore,
    private readonly eventBus: IEventBus,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly chainId: number,
    /**
     * Optional shared tick-distribution store wired through so every
     * `ManualClPositionStore` created in {@link upsertFromGraphql} gets
     * the same reference for its forward-looking `currentRate` computed.
     * Kept optional so unit tests that exercise orchestrator wiring
     * without the full namespace stack do not need to construct one.
     */
    private readonly tickDistributionStore?: ThickV2TickDistributionStore,
  ) {}

  initialize(): void {
    this.graphQlQuery = new OrchestratedQuery<ManualClPositionsSyncPayload>(
      "ManualCl Positions GraphQL Sync",
      (_q) => this.onLoadGraphQlTrigger(),
      30_000,
      this.onGraphQlSuccess.bind(this),
      this.onGraphQlFailure.bind(this),
      this.orchestratedQueryStore,
    )

    this.onChainQuery = new OrchestratedQuery<void>(
      "ManualCl Positions On-Chain Sync",
      (query) => this.onLoadOnChainTrigger(query),
      60_000,
      () => {},
      this.onOnChainFailure.bind(this),
      this.orchestratedQueryStore,
    )

    // NFPM tokenId discovery. Runs once per wallet session (plus on
    // lifecycle events) to map indexer `Position` rows to the real on-chain
    // NFPM tokenIds that the indexer doesn't yet expose. Cheaper than the
    // stats trigger so we poll less frequently.
    this.nfpmDiscoveryQuery = new OrchestratedQuery<void>(
      "ManualCl NFPM TokenId Discovery",
      (query) => this.onDiscoverNfpmTrigger(query),
      120_000,
      () => {},
      this.onDiscoveryFailure.bind(this),
      this.orchestratedQueryStore,
    )

    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    this.poolCreatedHandler = this.forceRefreshAll.bind(this)
    this.positionCreatedHandler = this.handlePositionCreated.bind(this)
    this.liquidityIncreasedHandler = this.forceRefreshAll.bind(this)
    this.liquidityDecreasedHandler = this.forceRefreshAll.bind(this)
    this.feesCollectedHandler = this.forceRefreshAll.bind(this)
    this.positionBurnedHandler = this.handlePositionBurned.bind(this)

    this.eventBus.subscribe(ManualClPoolCreatedEvent.TYPE, this.poolCreatedHandler)
    this.eventBus.subscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)
    this.eventBus.subscribe(ManualClLiquidityIncreasedEvent.TYPE, this.liquidityIncreasedHandler)
    this.eventBus.subscribe(ManualClLiquidityDecreasedEvent.TYPE, this.liquidityDecreasedHandler)
    this.eventBus.subscribe(ManualClFeesCollectedEvent.TYPE, this.feesCollectedHandler)
    this.eventBus.subscribe(ManualClPositionBurnedEvent.TYPE, this.positionBurnedHandler)

    // Manual CL no longer gated behind a feature flag. Queries only need a
    // connected wallet to have anything to read; wallet-connect below picks
    // up the case where the app boots without a wallet attached.
    if (this.walletStore.isConnected) {
      this.graphQlQuery.start()
      this.onChainQuery.start()
      this.nfpmDiscoveryQuery.start()
    }
  }

  get graphQlQueryHandle(): OrchestratedQuery<ManualClPositionsSyncPayload> | undefined {
    return this.graphQlQuery
  }

  get onChainQueryHandle(): OrchestratedQuery<void> | undefined {
    return this.onChainQuery
  }

  private handleWalletConnect(_event: WalletConnectedEvent): void {
    this.graphQlQuery?.start()
    this.onChainQuery?.start()
    this.nfpmDiscoveryQuery?.start()
    this.graphQlQuery?.forceExecute()
    this.nfpmDiscoveryQuery?.forceExecute()
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    this.graphQlQuery?.stop()
    this.onChainQuery?.stop()
    this.nfpmDiscoveryQuery?.stop()
    this.clearPostCreateRefreshTimers()
    runInAction(() => {
      this.positionRepository.reset()
    })
  }

  private forceRefreshAll(): void {
    this.graphQlQuery?.forceExecute()
    this.onChainQuery?.forceExecute()
    this.nfpmDiscoveryQuery?.forceExecute()
  }

  private handlePositionCreated(event: ManualClPositionCreatedEvent): void {
    const tokenId = event.payload.tokenId
    if (tokenId !== undefined) {
      this.positionRepository.markPostCreatePending(tokenId)
    }
    this.forceRefreshAll()
    for (const delayMs of [1_000, 3_000, 5_000, 10_000]) {
      this.postCreateRefreshTimers.push(setTimeout(() => this.forceRefreshAll(), delayMs))
    }
    if (tokenId !== undefined) {
      this.postCreateRefreshTimers.push(
        setTimeout(() => {
          runInAction(() => {
            this.positionRepository.clearPostCreatePending(tokenId)
          })
        }, 30_000),
      )
    }
  }

  private clearPostCreateRefreshTimers(): void {
    for (const timer of this.postCreateRefreshTimers) {
      clearTimeout(timer)
    }
    this.postCreateRefreshTimers = []
  }

  private handlePositionBurned(event: ManualClPositionBurnedEvent): void {
    const found = this.positionRepository.find(event.payload.tokenId)
    if (found) {
      runInAction(() => {
        found.markBurned()
      })
    }
    this.forceRefreshAll()
  }

  private async onLoadGraphQlTrigger(): Promise<ManualClPositionsSyncPayload> {
    const address = this.walletStore.connectedAddress?.toString()
    if (!address || !isAddress(address)) {
      return { positions: [], nfpmPositions: [], ethPriceUsd: undefined }
    }
    // Run both reads in parallel. `NfpmPosition` gives us the real NFP
    // tokenId and the indexer-computed underlying principals, replacing the
    // on-chain enumeration + simulated `decreaseLiquidity` entirely for
    // indexed positions.
    const [positionsPayload, nfpmPositions] = await Promise.all([
      this.graphQlTransport.loadPositionsForAddress(address, this.chainId),
      this.graphQlTransport
        .loadNfpmPositionsForAddress(address, this.chainId)
        // A stale indexer (pre-P7) should not break the `Position` feed.
        .catch((error: unknown) => {
          console.warn(
            "[ManualClPositionOrchestrator] NfpmPosition load failed; continuing without indexer principals",
            error,
          )
          return [] as Array<ManualClNfpmPositionRow>
        }),
    ])
    return {
      positions: positionsPayload.positions,
      nfpmPositions,
      ethPriceUsd: positionsPayload.ethPriceUsd,
    }
  }

  private onGraphQlSuccess(
    _sender: IOrchestratedQuerySender<ManualClPositionsSyncPayload>,
    data: ManualClPositionsSyncPayload,
  ): void {
    runInAction(() => {
      for (const item of data.positions) {
        try {
          this.mergeGraphQlRow(item, data.ethPriceUsd)
        } catch (error) {
          console.error(
            "[ManualClPositionOrchestrator.onGraphQlSuccess] Error processing row:",
            item.id,
            error,
          )
        }
      }
      // Fold indexer-native NFPM data onto the stores. Runs after the
      // `Position` merge so a freshly-upserted store can be matched by its
      // `(pool, tickLower, tickUpper)` tuple.
      this.mergeNfpmRows(data.nfpmPositions)
      this.positionRepository.markHydrated()
    })

    // First-paint fee nudge.
    //
    // On wallet connect the on-chain stats query fires *in parallel* with
    // the GraphQL sync, so its first invocation sees an empty repository
    // and exits without reading `tokensOwed`. Without an explicit nudge the
    // user then waits for the full 60 s polling interval before pending fees
    // arrive, even though the RPC itself takes ~100 ms.
    //
    // This fires the stats query the moment at least one position has a
    // resolved `nfpmTokenId` but no pending-fees snapshot. It self-stabilises:
    // once `pendingFees0` is populated `needsStats` is false and subsequent
    // GraphQL syncs don't re-trigger.
    if (this.hasPositionsAwaitingStats()) {
      this.onChainQuery?.forceExecute()
    }
  }

  /**
   * True when at least one active position has a resolved NFPM token id but
   * has never received a `pendingFees0/1` snapshot from the stats RPC. Drives
   * the post-merge nudge and the manage-page refresh so the collect button
   * unlocks within one round-trip instead of one polling interval.
   */
  private hasPositionsAwaitingStats(): boolean {
    return this.positionRepository
      .getAll()
      .some(
        (p) =>
          p.hasLiquidity &&
          p.liquidity > 0n &&
          p.nfpmTokenId !== undefined &&
          p.pendingFees0 === undefined,
      )
  }

  /**
   * Public nudge used by manage-page composites on mount. Triggers an
   * immediate on-chain stats read when the active position still has no
   * `pendingFees` data — keeps the collect button responsive regardless of
   * where in the 60 s polling window the user lands.
   *
   * Intentionally no-ops when every active position already has stats, so
   * repeated mounts (tab changes, route swaps) do not hammer the RPC.
   */
  refreshOnChainStats(): void {
    if (!this.hasPositionsAwaitingStats()) return
    this.onChainQuery?.forceExecute()
  }

  /**
   * Map each `NfpmPosition` row onto a `ManualClPositionStore`.
   *
   * A user can mint multiple NFTs into the same `(pool, tickLower,
   * tickUpper)` range over time. The indexer keeps one `NfpmPosition` row
   * per NFT (some closed, one active) but only a single aggregated
   * `Position` row per range. We therefore:
   *
   *   1. Drop closed / zero-liquidity NFPM rows outright — their principals
   *      are always `(0, 0)` and adopting their tokenId would point the UI
   *      at a dead NFT. They are not what the active `Position` row
   *      represents.
   *   2. Match what remains on `(pool address, tickLower, tickUpper)` and
   *      apply the snapshot. That tuple is unique per `Position` entity
   *      for a given wallet, so each live NFT lands on exactly one store.
   *
   * Side-effects per match:
   *   - Promote the real `nfpmTokenId` (if still pending).
   *   - Seed `simulatedUnderlying0/1` from `principal0/1`, which removes
   *     the dependency on the `nfpmUserStats` RPC for the balance side of
   *     the overview. Pending fees stay RPC-only.
   */
  private mergeNfpmRows(rows: Array<ManualClNfpmPositionRow>): void {
    if (rows.length === 0) return
    const liveRows = rows.filter((r) => !r.closed && r.liquidity > 0n)
    if (liveRows.length === 0) return
    for (const row of liveRows) {
      const poolAddress = row.pool.address.toLowerCase()
      const tickLower = row.tickLower
      const tickUpper = row.tickUpper
      const match = this.positionRepository
        .getAll()
        .find(
          (p) =>
            p.pool.address.raw.toLowerCase() === poolAddress &&
            p.tickLower === tickLower &&
            p.tickUpper === tickUpper,
        )
      if (!match) continue
      if (row.pool.tick !== undefined) {
        match.pool.setCurrentTick(row.pool.tick)
      }
      if (row.pool.sqrtPrice !== undefined) {
        match.pool.setCurrentSqrtPriceX96(row.pool.sqrtPrice)
      }
      if (row.pool.liquidity !== undefined) {
        try {
          match.pool.setCurrentLiquidity(BigInt(row.pool.liquidity))
        } catch {
          // Leave the last known active-liquidity snapshot in place.
        }
      }
      match.applyIndexerNfpmSnapshot({
        nfpmTokenId: row.tokenId,
        liquidity: row.liquidity,
        principal0: row.principal0,
        principal1: row.principal1,
        totalCollectedToken0: rawTokenAmountToNumber(row.totalCollected0, row.token0.decimals),
        totalCollectedToken1: rawTokenAmountToNumber(row.totalCollected1, row.token1.decimals),
        totalWithdrawnToken0: rawTokenAmountToNumber(row.totalWithdrawn0, row.token0.decimals),
        totalWithdrawnToken1: rawTokenAmountToNumber(row.totalWithdrawn1, row.token1.decimals),
      })
    }
  }

  private onGraphQlFailure(
    _sender: IOrchestratedQuerySender<ManualClPositionsSyncPayload>,
    error: Error,
  ): void {
    console.error("[ManualClPositionOrchestrator] GraphQL position sync failed", error)
  }

  private mergeGraphQlRow(
    item: ManualClMyPositionsResponseItem,
    ethPriceUsd: number | undefined,
  ): void {
    const poolAddress = new LcAddress(item.pool.address)
    const pool = this.poolRepository.find(poolAddress)
    if (!pool) return
    // `getMyPositions` already returns pool tick + sqrtPrice + liquidity.
    // Mirror those into the canonical pool store so position-value /
    // remove-liquidity math and per-position forward-looking APR have fresh
    // state even when the dedicated pool query is stale or delayed. The
    // `liquidity` field is what every my-positions row divides into to work
    // out its share of pool fees — without it, `currentRate` collapses to
    // 0.00% for every cross-pool row.
    pool.setCurrentTick(item.pool.tick)
    pool.setCurrentSqrtPriceX96(item.pool.sqrtPrice)
    if (item.pool.liquidity !== undefined) {
      try {
        pool.setCurrentLiquidity(BigInt(item.pool.liquidity))
      } catch {
        // Bad liquidity snapshot — leave the store's last known value in
        // place rather than zeroing it and silently breaking APRs.
      }
    }

    // Derive USD prices from indexer-backed references: `Bundle.ethPriceUSD`
    // (the chain's current ETH-in-USD) multiplied by each token's
    // `derivedETH` (the indexer's estimate of token→ETH). We mirror the
    // result onto the shared token store (so the wider app benefits) AND
    // stash it on the position itself — the position-level snapshot is the
    // authoritative source `positionValueUsd` reads from, so the value
    // renders the moment the graphql row lands even if the token store's
    // price pipeline is still racing to catch up.
    let token0UsdPrice: number | undefined
    let token1UsdPrice: number | undefined
    if (ethPriceUsd !== undefined && ethPriceUsd > 0) {
      const usd0 = item.token0.derivedETH * ethPriceUsd
      const usd1 = item.token1.derivedETH * ethPriceUsd
      if (Number.isFinite(usd0) && usd0 > 0) {
        pool.token0.updateOnChainPrice(usd0)
        token0UsdPrice = usd0
      }
      if (Number.isFinite(usd1) && usd1 > 0) {
        pool.token1.updateOnChainPrice(usd1)
        token1UsdPrice = usd1
      }
    }

    const dao: Omit<ManualClPositionStoreDAO, "positionId" | "tokenId" | "nfpmTokenId" | "pool"> = {
      tickLower: Number(item.tickLower),
      tickUpper: Number(item.tickUpper),
      liquidity: BigInt(item.liquidity),
      hasLiquidity: item.hasLiquidity,
      collectedFeesToken0: item.totalCollectedToken0,
      collectedFeesToken1: item.totalCollectedToken1,
      totalCollectedUsd: item.totalCollectedUSD,
      totalDepositedToken0: item.totalDepositedToken0,
      totalDepositedToken1: item.totalDepositedToken1,
      totalWithdrawnToken0: item.totalWithdrawnToken0,
      totalWithdrawnToken1: item.totalWithdrawnToken1,
      createdAtTimestamp: item.createdAtTimestamp,
      lastActivityTimestamp: item.lastActivityTimestamp,
    }

    const nfpmTokenId = parseNfpmTokenId(item.nfpmTokenIds)
    const existing = this.positionRepository.find(item.id)
    if (existing) {
      existing.pool = pool
      existing.updateFromGraphQl(dao)
      existing.setGraphPrices(token0UsdPrice, token1UsdPrice)
      existing.setNfpmTokenId(nfpmTokenId)
      return
    }
    const tokenId = nfpmTokenId ?? syntheticTokenIdFromPositionId(item.id)
    const position = new ManualClPositionStore(
      {
        ...dao,
        positionId: item.id,
        tokenId,
        nfpmTokenId,
        pool,
      },
      this.tickDistributionStore,
    )
    position.setGraphPrices(token0UsdPrice, token1UsdPrice)
    this.positionRepository.upsert(position)
  }

  private async onLoadOnChainTrigger(query: IOrchestratedQuerySender<void>): Promise<void> {
    const address = this.walletStore.connectedAddress?.toViemAddress()
    if (!address) return
    // This RPC pass exists primarily to read live pending fees
    // (`tokensOwed0/1`) that the indexer cannot derive from events. The
    // underlying balances it also returns are a cross-check against the
    // indexer's `principal0/1` — they should agree within a couple of wei.
    //
    // Skip positions whose real NFPM tokenId is still undiscovered. Passing
    // a synthetic id to the NFPM simulator reverts ("Invalid token ID") and
    // burns RPC credits for no benefit.
    const positions = this.positionRepository
      .getAll()
      .filter((p) => p.hasLiquidity && p.liquidity > 0n && p.nfpmTokenId !== undefined)
    if (positions.length === 0) return
    const stats = await this.onChainTransport.loadPositionStats(
      positions.map((p) => ({ tokenId: p.nfpmTokenId as bigint, liquidity: p.liquidity })),
      {
        account: address,
        chainId: this.chainId,
        nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
      },
      query,
    )
    runInAction(() => {
      for (const entry of stats) {
        const position = this.positionRepository
          .getAll()
          .find((p) => p.nfpmTokenId === entry.tokenId)
        if (!position) continue
        position.applyOnChainStats({
          pendingFees0: entry.result.token0.fees,
          pendingFees1: entry.result.token1.fees,
          underlying0: entry.result.token0.balance,
          underlying1: entry.result.token1.balance,
        })
      }
    })
  }

  private onOnChainFailure(_sender: IOrchestratedQuerySender<void>, error: Error): void {
    console.error("[ManualClPositionOrchestrator] On-chain position stats failed", error)
  }

  /**
   * Enumerate every NFPM NFT the connected wallet owns, then match each one
   * back to its indexer `Position` row by `(poolAddress, tickSpacing,
   * tickLower, tickUpper)` and promote the real `nfpmTokenId` onto the
   * store. Once this completes, `nfpmTokenIdPending` flips to `false` and
   * the UI unlocks all action buttons.
   */
  private async onDiscoverNfpmTrigger(_query: IOrchestratedQuerySender<void>): Promise<void> {
    const address = this.walletStore.connectedAddress?.toViemAddress()
    if (!address) return
    const positions = this.positionRepository.getAll()
    if (positions.length === 0) return
    // Only hit the chain when we actually have something pending. If every
    // row already has a tokenId (Proposal 4 live, or a re-run) there's
    // nothing to do.
    const hasPending = positions.some((p) => p.nfpmTokenIdPending)
    if (!hasPending) return

    const discoveryResult = await this.onChainTransport.discoverNfpmPositions({
      account: address,
      chainId: this.chainId,
      nfpManagerAddress: THICK_V2_BASE_ADDRESSES.nfPositionManager,
    })
    runInAction(() => {
      this.positionRepository.setNfpmDiscovery({
        totalOwned: discoveryResult.totalOwned,
        scanned: discoveryResult.scanned,
        truncated: discoveryResult.truncated,
      })
    })
    const discovered = discoveryResult.positions
    if (discovered.length === 0) return

    runInAction(() => {
      let matchedAny = false
      for (const position of positions) {
        if (!position.nfpmTokenIdPending) continue
        const tickSpacing = position.pool.tickSpacing
        const poolToken0 = position.pool.token0.address.raw.toLowerCase()
        const poolToken1 = position.pool.token1.address.raw.toLowerCase()
        // Primary match: same tick spacing + tick range AND same token pair. The
        // NFPM tuple records token0/token1 in the pool's canonical order,
        // so a direct comparison is safe.
        const match =
          discovered.find(
            (d) =>
              d.tickSpacing === tickSpacing &&
              d.tickLower === position.tickLower &&
              d.tickUpper === position.tickUpper &&
              d.token0.toLowerCase() === poolToken0 &&
              d.token1.toLowerCase() === poolToken1,
          ) ??
          // Fallback: ignore the token pair. There is exactly one pool per
          // (token0, token1, tickSpacing) so when the wallet holds a single NFT
          // in the same tick spacing + range we can still resolve it unambiguously.
          discovered.find(
            (d) =>
              d.tickSpacing === tickSpacing &&
              d.tickLower === position.tickLower &&
              d.tickUpper === position.tickUpper,
          )
        if (match) {
          position.setNfpmTokenId(match.tokenId)
          matchedAny = true
        }
        // Positions still unresolved stay pending (yellow triangle).
      }
      if (matchedAny) {
        // Nudge the stats query so the overview populates without waiting
        // for the 60s polling interval.
        this.onChainQuery?.forceExecute()
      }
    })
  }

  private onDiscoveryFailure(_sender: IOrchestratedQuerySender<void>, error: Error): void {
    console.error("[ManualClPositionOrchestrator] NFPM tokenId discovery failed", error)
  }
}

function rawTokenAmountToNumber(value: bigint, decimals: number): number {
  const formatted = Number(formatUnits(value, decimals))
  return Number.isFinite(formatted) ? formatted : 0
}

/**
 * Parse the indexer `nfpmTokenIds` array. The entity stores every NFP that
 * has ever minted into the same tick range — in almost all cases there is
 * exactly one. We pick the most recent (highest id) to represent the
 * position for UI and contract-call purposes.
 *
 * Returns `undefined` when the array is empty (pre-Proposal 4 or
 * un-indexed mint), signalling the "pending" state.
 */
function parseNfpmTokenId(ids: Array<string | number> | undefined): bigint | undefined {
  if (!ids || ids.length === 0) return undefined
  let best: bigint | undefined = undefined
  for (const raw of ids) {
    try {
      const candidate = typeof raw === "bigint" ? raw : BigInt(raw)
      if (best === undefined || candidate > best) best = candidate
    } catch {
      // skip malformed entries rather than blowing up the whole merge.
    }
  }
  return best
}

/**
 * Deterministic synthetic bigint derived from the GraphQL positionId. Used
 * as the routing / repository key until Proposal 4 provides a real NFP id.
 *
 * djb2 hash mixed into a 64-bit space and tagged with the high bit so it is
 * trivially distinguishable from real NFP token ids (which are allocated
 * sequentially and stay comfortably below 2^63).
 */
function syntheticTokenIdFromPositionId(positionId: string): bigint {
  let hash = 5381n
  for (let i = 0; i < positionId.length; i++) {
    hash = ((hash << 5n) + hash + BigInt(positionId.charCodeAt(i))) & 0xffffffffffffffffn
  }
  return hash | (1n << 63n)
}
